/**
 * Keeping a live camera actually painting inside a `<video>` that has been moved.
 *
 * The кружочек's circle is not a second camera: RecordPreview takes the element
 * the take is already decoding through and moves it into the circle, because the
 * element belongs to the recording rather than to React (see startVideoNote).
 * Moving a `<video>` between parents is where WebKit loses it. The element goes
 * on holding a live MediaStream, the file goes on being written from the track
 * and comes out perfect — and the circle shows the placeholder underneath it,
 * which is the grey disc this exists to stop.
 *
 * There are two known ways it goes grey and this deliberately does not try to
 * tell them apart. WebKit pauses an element that leaves the document, and it
 * does so on a queued task rather than on the spot: the element is re-inserted,
 * played, and *then* paused — which is exactly the shape of a fault that happens
 * on some takes and not others, and why playing it once after the move was not
 * enough. Separately, an element that has been through a renderer teardown can
 * come back with no display layer, and then it is playing, `readyState` is 4,
 * `paused` is false, and nothing is drawn. No property reports that state.
 *
 * So this watches the only thing that answers the question actually being asked.
 * `readyState` says the element *could* paint; `requestVideoFrameCallback` fires
 * when it *did*. A gap with no frames in it is the grey circle, whatever caused
 * it, and the repair is the same either way: re-attach the stream, which is the
 * strongest kick short of building a new element — and, not coincidentally, what
 * a flip mid-take was applying on the takes where flipping happened to bring the
 * picture back.
 *
 * None of this touches the recording. The tracks belong to the take; assigning
 * `srcObject` hands the same ones back to an element that is only watching them.
 */

/**
 * `requestVideoFrameCallback`, which TypeScript's DOM library still does not
 * carry and Firefox still does not implement. Same shape as the one `firstFrame`
 * declares in capture.ts, for the same reason.
 */
type FrameElement = HTMLVideoElement & {
  requestVideoFrameCallback?: (callback: () => void) => number;
  cancelVideoFrameCallback?: (handle: number) => void;
};

/**
 * How long the circle may stay empty after the move before the element is
 * treated as broken rather than as slow.
 *
 * The camera is already open and already playing by the time the circle exists —
 * the take waited for `play()` before it started recording — so the first frame
 * here is a frame or two away, not a device away. Nine tenths of a second is far
 * past that and short of anything a sender would sit through wondering.
 */
const FIRST_FRAME_MS = 900;

/**
 * And how long a gap between frames is a picture that has stopped rather than a
 * camera having a moment. The same 1.2s the canvas-era watchdog used, for the
 * same reason: it is longer than any hiccup and shorter than a person's patience.
 */
const STALL_MS = 1_200;

/**
 * How many times one take may re-attach the stream before the element is left
 * alone. Three is the point at which the problem is not one this can fix, and
 * carrying on would be a `<video>` being reloaded every second for a minute
 * behind a circle that is grey anyway.
 *
 * Consecutive, not cumulative: a frame arriving means the repair worked, and a
 * camera that hiccups four times over a minute gets four repairs.
 */
const MAX_REVIVALS = 3;

/**
 * How long after a repair a `pause` is the repair's own rather than the
 * browser's. Long enough for a queued media element task, short enough that a
 * real pause a moment later is still counted — and it is played again either
 * way, since only the tally is at stake.
 */
const REPAIR_ECHO_MS = 250;

/** What a take had to be rescued from, for the record. Nulls when nothing did. */
export interface PreviewTrouble {
  /** Times WebKit paused the element behind our back and it was played again. */
  paused: number;
  /** Times nothing was painting and the stream was re-attached to fix it. */
  revivals: number;
  /**
   * Whether the circle ever showed anything at all. False is the whole
   * complaint: a take filmed against a grey disc, with a perfectly good file at
   * the end of it. True on a browser with no way to tell, which is one that has
   * never had the fault.
   */
  painted: boolean;
}

/**
 * Watches the element for as long as the circle is on screen, and repairs it.
 *
 * Call it once the element is in place; the returned function stops the watch
 * and hands back what went wrong, or null if the answer is nothing. It plays the
 * element itself, so the caller does not need to.
 */
export function keepPainting(video: HTMLVideoElement): () => PreviewTrouble | null {
  const element = video as FrameElement;

  let paused = 0;
  let revivals = 0;
  /** Since the last frame, which is what the budget above is counted in. */
  let sinceFrame = 0;
  let painted = false;
  let stopped = false;
  /** When the stream was last re-attached, so its own `pause` is not counted. */
  let repairedAt = Number.NEGATIVE_INFINITY;
  let deadline = 0;
  let frame = 0;

  const play = () => {
    void video.play().catch(() => {});
  };

  // The queued pause described at the top. Cheap, and it catches the case
  // before the deadline does — a paused element is grey for as long as it takes
  // the watchdog to notice, and this makes that nothing.
  const onPause = () => {
    if (stopped) return;
    // Played back whatever queued it, and counted only when nothing here did.
    // Re-attaching the stream fires one of these itself — the load algorithm
    // pauses an element that was playing, on a queued task rather than on the
    // spot — and counting that would make the repair look like the fault. The
    // window rather than a flag for the same reason: by the time the event
    // arrives, a flag set around the assignment has long since been cleared.
    if (performance.now() - repairedAt > REPAIR_ECHO_MS) paused += 1;
    play();
  };

  const arm = (ms: number) => {
    window.clearTimeout(deadline);
    deadline = window.setTimeout(revive, ms);
  };

  const watch = () => {
    if (frame !== 0) element.cancelVideoFrameCallback?.(frame);
    frame = element.requestVideoFrameCallback!(onFrame);
  };

  function onFrame(): void {
    frame = 0;
    if (stopped) return;
    painted = true;
    sinceFrame = 0;
    arm(STALL_MS);
    watch();
  }

  function revive(): void {
    if (stopped || sinceFrame >= MAX_REVIVALS) return;
    sinceFrame += 1;
    revivals += 1;

    // Handing the same tracks back rather than building a new element: this is
    // what rebuilds the media player under WebKit, and it is invisible from the
    // recording's side. A missing source means the take has already ended and
    // released everything, in which case there is nothing left to repair.
    const stream = video.srcObject;
    if (stream) {
      repairedAt = performance.now();
      video.srcObject = null;
      video.srcObject = stream;
    }
    play();
    watch();
    arm(FIRST_FRAME_MS);
  }

  play();

  video.addEventListener('pause', onPause);
  // Firefox, where there is no way to ask whether a frame was drawn. It is also
  // the one engine this fault has never been seen on, so the watch is skipped
  // rather than faked with a timer that would only ever guess.
  const observable = typeof element.requestVideoFrameCallback === 'function';
  if (observable) {
    watch();
    arm(FIRST_FRAME_MS);
  }

  return () => {
    stopped = true;
    video.removeEventListener('pause', onPause);
    window.clearTimeout(deadline);
    if (frame !== 0) element.cancelVideoFrameCallback?.(frame);
    if (!observable) return null;
    return paused === 0 && revivals === 0 && painted ? null : { paused, revivals, painted };
  };
}


/**
 * The same watch, on a picture that has to survive the app being put away.
 *
 * A call outlives the screen it is drawn on: an Android app minimised for a
 * second and brought back returns to `<video>` elements that hold live streams
 * and paint nothing — measured on a Honor against an iPad, where the far end
 * went on receiving frames the whole time and the phone showed black, first on
 * one picture and then on both. Toggling the camera does not help, because the
 * fault is in the element rather than in the capture.
 *
 * `keepPainting` is the right repair, and starting it once for the length of the
 * call is the wrong way to use it here: a backgrounded WebView paints nothing at
 * all, so the watchdog would spend its three revivals while nobody is looking
 * and have none left at the moment they are needed. So the watch is bound to
 * visibility instead — stopped when the page is hidden, started afresh when it
 * comes back, which is exactly when a fresh budget is wanted.
 *
 * `onTrouble` is called for each stretch that needed rescuing, and not at all
 * for the ordinary case; the caller files it against the call.
 */
export function keepPaintingWhileVisible(
  video: HTMLVideoElement,
  onTrouble: (trouble: PreviewTrouble) => void,
): () => void {
  let stop: (() => PreviewTrouble | null) | null = null;

  const start = () => {
    stop ??= keepPainting(video);
  };

  const halt = () => {
    const trouble = stop?.();
    stop = null;
    if (trouble) onTrouble(trouble);
  };

  const onVisibility = () => {
    if (document.visibilityState === 'visible') start();
    else halt();
  };

  if (document.visibilityState === 'visible') start();
  document.addEventListener('visibilitychange', onVisibility);

  return () => {
    document.removeEventListener('visibilitychange', onVisibility);
    halt();
  };
}

/** The trouble as one line, for the note filed against the take. */
export function describeTrouble(trouble: PreviewTrouble): string {
  return [
    trouble.painted ? 'painted' : 'never painted',
    `paused ×${trouble.paused}`,
    `revived ×${trouble.revivals}`,
  ].join(', ');
}
